import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { getAllPlatforms } from '../platform/controller/platform-controller';
import { FaSearch, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const PlatformSelector = ({ selectedPlatform, onChange, error }) => {
  const [platforms, setPlatforms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPlatformName, setSelectedPlatformName] = useState('');
  const dropdownRef = useRef(null);
  const dropdownPositionRef = useRef(null);
  const searchInputRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });

  // Fetch platforms ONLY on component mount
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        setLoading(true);
        const data = await getAllPlatforms();
        const platformList = Array.isArray(data) ? data : [];
        setPlatforms(platformList);

        if (selectedPlatform) {
          const platform = platformList.find(p => p.id.toString() === selectedPlatform.toString());
          if (platform) {
            const type = platform.typePlateform ? ` (${platform.typePlateform})` : '';
            setSelectedPlatformName(`${platform.name}${type}`);
          }
        }
      } catch (err) {
        console.error('Failed to fetch platforms:', err);
        setFetchError('Impossible de charger les plateformes');
      } finally {
        setLoading(false);
      }
    };

    fetchPlatforms();
  }, []); // Empty dependency array - only runs once on mount

  // Update selectedPlatformName when selectedPlatform changes
  useEffect(() => {
    if (selectedPlatform && platforms.length > 0) {
      const platform = platforms.find(p => p.id.toString() === selectedPlatform.toString());
      if (platform) {
        setSelectedPlatformName(platform.name);
      }
    }
  }, [selectedPlatform, platforms]);

  // Update dropdown position when it's opened or when scrolling
  useEffect(() => {
    const updatePosition = () => {
      if (dropdownPositionRef.current && isOpen) {
        const rect = dropdownPositionRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + window.scrollY,
          left: rect.left + window.scrollX,
          width: rect.width
        });
      }
    };

    updatePosition();

    // Add scroll and resize event listeners
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);


  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter platforms based on search term
  const filteredPlatforms = platforms.filter(platform =>
    platform.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle platform selection
  const handleSelectPlatform = (platform) => {
    const event = {
      target: {
        name: 'platformId',
        value: platform.id.toString()
      }
    };
    onChange(event);
    const type = platform.typePlateform ? ` (${platform.typePlateform})` : '';
    setSelectedPlatformName(`${platform.name}${type}`);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Toggle dropdown
  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  return (
    <div className="relative">
      <label htmlFor="platformId" className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
        Plateforme <span className="text-red-500 ml-1">*</span>
      </label>
      <div ref={dropdownPositionRef} className="relative">
        <div className="relative group">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-4 w-4 text-gray-400 group-hover:text-[#174540] transition-colors duration-200" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
            </svg>
          </div>
          <div
            onClick={toggleDropdown}
            className={`pl-10 py-3 shadow-sm flex items-center justify-between w-full border-0 border-b-2 border-gray-200 hover:bg-gray-50 text-sm rounded-t-md bg-gray-50 cursor-pointer ${
              error ? 'border-red-300 text-red-900' : 'border-gray-200'
            }`}
          >
            <span className={selectedPlatformName ? 'text-gray-900' : 'text-gray-400'}>
              {selectedPlatformName || 'Sélectionnez une plateforme'}
            </span>
            <div className="flex items-center">
              {loading && (
                <svg className="animate-spin h-4 w-4 mr-2 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              )}
              {isOpen ? (
                <FaChevronUp className="h-4 w-4 text-gray-500" />
              ) : (
                <FaChevronDown className="h-4 w-4 text-gray-500" />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Render dropdown using a portal */}
      {isOpen &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="absolute bg-white rounded-md shadow-lg border border-gray-200 overflow-hidden"
            style={{
              position: 'absolute',
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: dropdownPosition.width,
              zIndex: 9999,
              maxHeight: '300px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
            }}
          >
            {/* Search bar */}
            <div className="sticky top-0 p-2 bg-white border-b border-gray-100 z-10">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaSearch className="h-3.5 w-3.5 text-gray-400" />
                </div>
                <input
                  type="text"
                  className="pl-10 pr-3 py-2 w-full text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#174540] focus:border-[#174540]"
                  placeholder="Rechercher..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  ref={searchInputRef}
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>

            {/* Platform list */}
            <div className="overflow-y-auto" style={{ maxHeight: '250px' }}>
              {loading && !platforms.length ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  Chargement des plateformes...
                </div>
              ) : filteredPlatforms.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  Aucune plateforme trouvée
                </div>
              ) : (
                <ul className="py-1">
                  {filteredPlatforms.map((platform) => (
                    <li
                      key={platform.id}
                      onClick={() => handleSelectPlatform(platform)}
                      className={`px-4 py-2 text-sm cursor-pointer flex items-center ${
                        selectedPlatform === platform.id.toString()
                          ? 'bg-[#f5f8f7] text-[#174540]'
                          : 'text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      <div className="w-7 h-7 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mr-3">
                        <span className="text-xs font-medium text-emerald-800">
                          {platform.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <span className="truncate">
                        {platform.name}
                        {platform.typePlateform && (
                          <span className="ml-2 text-xs text-gray-400">({platform.typePlateform})</span>
                        )}
                      </span>
                      {selectedPlatform === platform.id.toString() && (
                        <svg className="ml-auto h-4 w-4 text-[#174540]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )}
      {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
      {fetchError && !error && <p className="mt-2 text-sm text-amber-600">{fetchError}</p>}
    </div>
  );
};

export default PlatformSelector;